import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CategoryItem } from '../types';
import { CategoryNode } from './CategoryNode';
import { AddItemInput } from './AddItemInput';
import { AITreeGenerator } from './AITreeGenerator';
import { MoveToModal } from './MoveToModal';
import { apiClient } from '../services/api';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';
import { buildTree, updateTreeItem, removeTreeItem, moveTreeItem, findTreeItem, getExpandedIds, moveItemAcrossCategories } from '../utils/treeUtils';
import { Loader2, Sparkles, Tag, Check, X, Trash2, Move, Search, BookText, MessageSquare } from 'lucide-react';
import { trackPageViewed } from '../utils/analytics';
import { SHOW_BULK_ENRICH } from '../constants/uiVisibility';
import { SearchCardResult } from '../services/api';
import { CardDetail } from './CardDetail';
import { GenerateStoryModal } from './GenerateStoryModal';
import { CardSelectionContextualPracticeModal } from './CardSelectionContextualPracticeModal';

export const VocabTree: React.FC = () => {
  const { selectedInstanceId, instancesWithRoles } = useInstance();
  const { user } = useAuth();

  useEffect(() => { trackPageViewed('library'); }, []);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  /** When set, the matching CategoryNode opens its category view (e.g. after AI tree generation). */
  const [openCategoryViewId, setOpenCategoryViewId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const isInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState<Set<number>>(new Set());
  const [isBulkEnriching, setIsBulkEnriching] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [showBulkGenerateText, setShowBulkGenerateText] = useState(false);
  const [showBulkContextualPractice, setShowBulkContextualPractice] = useState(false);

  // Derive which categories the selected cards belong to
  const selectedCardCategoryIds = useMemo(() => {
    if (selectedCardIds.size === 0) return new Set<number>();
    const catIds = new Set<number>();
    for (const category of categories) {
      for (const id of Array.from(selectedCardIds)) {
        if (findTreeItem(category.children, id)) {
          catIds.add(category.id);
          break;
        }
      }
    }
    return catIds;
  }, [selectedCardIds, categories]);

  const canGenerateText = selectedCardIds.size > 0 && selectedCardCategoryIds.size === 1;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchCardResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCardId, setSearchCardId] = useState<number | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const categoriesRef = useRef<CategoryItem[]>([]);

  const BULK_ENRICH_LIMIT = 10;

  // Keep ref in sync with state
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim() || !selectedInstanceId) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const data = await apiClient.searchCards({ q: searchQuery.trim(), instance_id: selectedInstanceId });
        setSearchResults(data.items);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, selectedInstanceId]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadCategories = useCallback(async (options?: { silent?: boolean }): Promise<CategoryItem[] | undefined> => {
    if (selectedInstanceId === null) return undefined;

    const silent = options?.silent === true;

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      
      const categoriesWithCards = await apiClient.getCategoriesWithCards(selectedInstanceId);

      const categoriesWithChildren = categoriesWithCards.map((item) => {
        // Handle null category (uncategorized items)
        if (!item.category) {
          const treeData = buildTree(item.cards || []);
          return {
            id: 0, // Use 0 for uncategorized items
            name: 'Uncategorized',
            isExpanded: false,
            children: treeData,
          };
        }
        
        // Handle regular category
        const treeData = buildTree(item.cards || []);
        return {
          id: item.category.id,
          name: item.category.name,
          isExpanded: false,
          children: treeData,
        };
      });
      
      setCategories(categoriesWithChildren);
      return categoriesWithChildren;
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError(`Failed to load categories: ${err.message || err.response?.data?.detail || 'Unknown error'}`);
      return undefined;
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [selectedInstanceId]);

  // Load initial data
  useEffect(() => {
    if (selectedInstanceId !== null) {
      loadCategories();
    }
  }, [selectedInstanceId, loadCategories]);

  const handleToggle = useCallback((id: number) => {
    setCategories(prevCategories => 
      prevCategories.map(category => {
        if (category.id === id) {
          return { ...category, isExpanded: !category.isExpanded };
        }
        return {
          ...category,
          children: updateTreeItem(category.children, id, { 
            isExpanded: !findTreeItem(category.children, id)?.isExpanded 
          })
        };
      })
    );
  }, []);

  const handleRename = useCallback(async (id: number, newName: string) => {
    try {
      // Check if this is a category rename
      // Note: CategoryNode already makes the API call for categories, so we only update UI
      // For non-categories (cards/folders), we need to make the API call
      const isCategory = categories.some(cat => cat.id === id);
      
      if (!isCategory) {
        // For non-categories, make the API call
        await apiClient.updateCard(id, { name: newName });
      }
      
      // Update UI state for both categories and non-categories
      setCategories(prevCategories => 
        prevCategories.map(category => {
          // Check if this is a category rename
          if (category.id === id) {
            return { ...category, name: newName };
          }
          // Otherwise, update children
          return {
            ...category,
            children: updateTreeItem(category.children, id, { name: newName })
          };
        })
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to rename item');
      console.error('Error renaming item:', err);
    }
  }, [categories]);

  const handleDelete = useCallback((id: number) => {
    // This function is called after the API delete has already been handled by handleConditionalDelete
    // We just need to update the UI state
    console.log(`VocabTree handleDelete called for item ${id} - updating UI only`);
    setCategories(prevCategories => 
      prevCategories.map(category => ({
        ...category,
        children: removeTreeItem(category.children, id)
      }))
    );
  }, []);

  const handleCategoryRefresh = useCallback(async (categoryId: number, expandFolderId?: number) => {
    try {
      setCategories(prevCategories => {
        // Find the category to get current expanded state
        const currentCategory = prevCategories.find(cat => cat.id === categoryId);
        
        // Get currently expanded IDs
        const expandedIds = currentCategory ? getExpandedIds(currentCategory.children) : new Set<number>();
        
        // If we have a folder to expand, add it to the set
        if (expandFolderId) {
          expandedIds.add(expandFolderId);
        }
        
        return prevCategories; // Return unchanged for now, will update after fetch
      });
      
      const updatedCards = await apiClient.getCardsHierarchy(categoryId, selectedInstanceId || undefined);
      
      setCategories(prevCategories => {
        const currentCategory = prevCategories.find(cat => cat.id === categoryId);
        const expandedIds = currentCategory ? getExpandedIds(currentCategory.children) : new Set<number>();
        
        if (expandFolderId) {
          expandedIds.add(expandFolderId);
        }
        
        const updatedTreeData = buildTree(updatedCards, expandedIds);
        
        return prevCategories.map(category => {
          if (category.id === categoryId) {
            return {
              ...category,
              children: updatedTreeData,
              isExpanded: true, // Auto-expand when adding children
            };
          }
          return category;
        });
      });
    } catch (err: any) {
      console.error('Error refreshing category:', err);
    }
  }, []);

  const handleAddCategory = async (name: string) => {
    if (selectedInstanceId === null) {
      setError('Please select an instance before adding a category');
      return;
    }

    try {
      const newCategory = await apiClient.createCategory(name, selectedInstanceId, false);
      const newCategoryItem: CategoryItem = {
        id: newCategory.id,
        name: newCategory.name,
        isExpanded: false,
        children: [],
      };

      setCategories(prevCategories => [...prevCategories, newCategoryItem]);
      setShowAddCategory(false);
    } catch (err: any) {
      console.error('Error adding category:', err);
      setError(`Failed to add category: ${err.message || err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleMove = useCallback(async (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => {
    // Store original state for rollback on error (deep clone using JSON)
    const originalCategories = JSON.parse(JSON.stringify(categoriesRef.current));
    
    // Find the item name for the success message
    let itemName = '';
    for (const category of categoriesRef.current) {
      const found = findTreeItem(category.children, itemId);
      if (found) {
        itemName = found.name;
        break;
      }
    }
    
    // Optimistically update UI
    setCategories(prevCategories => {
      const updated = moveItemAcrossCategories(
        prevCategories,
        itemId,
        data.parent_id ?? null,
        data.category_id ?? null
      );
      // Update ref with new state
      categoriesRef.current = updated;
      return updated;
    });
    
    try {
      await apiClient.moveCard(itemId, data);
      // Success - UI already updated optimistically, show success message
      setSuccessMessage(`"${itemName}" moved successfully`);
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      // Rollback on error
      setCategories(originalCategories);
      categoriesRef.current = originalCategories;
      setError(err.response?.data?.detail || 'Failed to move item');
      console.error('Error moving item:', err);
      throw err;
    }
  }, []);



  const handleSelectCard = useCallback((id: number, selected: boolean) => {
    setSelectedCardIds(prev => {
      const next = new Set(prev);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedCardIds(new Set());
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedCardIds.size === 0) return;
    const confirmed = window.confirm(`Delete ${selectedCardIds.size} card${selectedCardIds.size !== 1 ? 's' : ''}? This cannot be undone.`);
    if (!confirmed) return;
    setIsBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        Array.from(selectedCardIds).map((id) => apiClient.deleteCard(id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = results.length - failed;
      const message = failed > 0
        ? `${succeeded} card${succeeded !== 1 ? 's' : ''} deleted, ${failed} failed`
        : `${succeeded} card${succeeded !== 1 ? 's' : ''} deleted`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 4000);
      handleExitSelectionMode();
      await loadCategories({ silent: true });
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedCardIds, handleExitSelectionMode, loadCategories]);

  const handleBulkMove = useCallback(async (
    _itemId: number,
    data: { parent_id?: number | null; category_id?: number | null }
  ) => {
    await Promise.all(
      Array.from(selectedCardIds).map((id) => apiClient.moveCard(id, data))
    );
    setShowBulkMoveModal(false);
    setSuccessMessage(`${selectedCardIds.size} card${selectedCardIds.size !== 1 ? 's' : ''} moved`);
    setTimeout(() => setSuccessMessage(null), 4000);
    handleExitSelectionMode();
    await loadCategories({ silent: true });
  }, [selectedCardIds, handleExitSelectionMode, loadCategories]);

  const handleBulkEnrich = useCallback(async () => {
    if (selectedCardIds.size === 0) return;
    setIsBulkEnriching(true);
    try {
      const result = await apiClient.aiEnrichBulk(Array.from(selectedCardIds), 'english');
      const message = result.failed > 0
        ? `${result.enriched} card${result.enriched !== 1 ? 's' : ''} enriched, ${result.failed} failed`
        : `All ${result.enriched} card${result.enriched !== 1 ? 's' : ''} enriched successfully`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 4000);
      handleExitSelectionMode();
      await loadCategories({ silent: true });
    } catch {
      setSuccessMessage('Failed to enrich cards. Please try again.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } finally {
      setIsBulkEnriching(false);
    }
  }, [selectedCardIds, handleExitSelectionMode, loadCategories]);

  const handleCategoryViewRequestHandled = useCallback(() => {
    setOpenCategoryViewId(null);
  }, []);

  const handleAIGeneratorSuccess = useCallback(
    async (newCategoryId?: number) => {
      const prevIds = new Set(categoriesRef.current.map((c) => c.id));
      const next = await loadCategories({ silent: true });

      let targetId = newCategoryId;
      if (targetId == null && next) {
        const novel = next.filter((c) => c.id !== 0 && !prevIds.has(c.id));
        if (novel.length === 1) {
          targetId = novel[0].id;
        }
      }
      if (targetId != null) {
        setOpenCategoryViewId(targetId);
      }
    },
    [loadCategories]
  );

  // Note: flattenTree utility is available for future drag and drop enhancements

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Loading vocabulary tree...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
        <button
          onClick={() => loadCategories()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Success Toast Notification */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg px-4 py-3 flex items-center space-x-2 min-w-[250px] max-w-md">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-sm font-medium text-green-800 flex-1">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Library</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Browse and organize your vocabulary</p>
        </div>
        {isInstanceOwner && (
          <div className="flex items-center space-x-2">
            {!isSelectionMode ? (
              <>
                {categories.length > 0 && (
                  <button
                    onClick={() => setIsSelectionMode(true)}
                    className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <Check className="h-4 w-4 shrink-0" />
                    <span>Select</span>
                  </button>
                )}
                <button
                  onClick={() => setShowAIGenerator(true)}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors"
                >
                  <Sparkles className="h-4 w-4 shrink-0" />
                  <span className="sm:hidden">AI</span>
                  <span className="hidden sm:inline">Generate with AI</span>
                </button>
                <button
                  onClick={() => setShowAddCategory(true)}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  <Tag className="h-4 w-4 shrink-0" />
                  <span>Add Category</span>
                </button>
              </>
            ) : (
              <button
                onClick={handleExitSelectionMode}
                className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
              >
                <X className="h-4 w-4 shrink-0" />
                <span>Cancel</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Search bar — only when there's something to search */}
      {selectedInstanceId && categories.length > 0 && (
        <div ref={searchRef} className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search cards in this collection…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-300"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Results dropdown */}
          {searchQuery.trim() && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-40 max-h-72 overflow-y-auto">
              {isSearching ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No cards found.</p>
              ) : (
                <ul>
                  {searchResults.map(result => {
                    const categoryName = categories.find(c => c.id === result.category_id)?.name;
                    return (
                      <li key={result.id}>
                        <button
                          onClick={() => { setSearchCardId(result.id); setSearchQuery(''); setSearchResults([]); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-0"
                        >
                          <p className="text-sm font-medium text-gray-800">{result.name}</p>
                          {categoryName && (
                            <p className="text-xs text-gray-400 mt-0.5">{categoryName}</p>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {isSelectionMode && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 sm:px-4 sm:py-3">
          <span className="text-sm text-blue-800 font-medium whitespace-nowrap mr-2">
            {selectedCardIds.size} <span className="hidden sm:inline">card{selectedCardIds.size !== 1 ? 's' : ''} </span>selected
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Move */}
            <button
              onClick={() => setShowBulkMoveModal(true)}
              disabled={selectedCardIds.size === 0}
              title="Move to..."
              className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Move className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">Move to...</span>
            </button>

            {/* Generate Text */}
            <div className="relative group/gentext">
              <button
                onClick={() => setShowBulkGenerateText(true)}
                disabled={!canGenerateText}
                title="Generate Text"
                className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <BookText className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Generate Text</span>
              </button>
              {selectedCardIds.size > 0 && !canGenerateText && (
                <div className="absolute right-0 top-full mt-1 z-10 w-52 bg-gray-800 text-white text-xs rounded-md px-2 py-1.5 hidden group-hover/gentext:block pointer-events-none">
                  Select cards from one category only
                </div>
              )}
            </div>

            {/* Q&A Practice */}
            <button
              onClick={() => setShowBulkContextualPractice(true)}
              disabled={selectedCardIds.size === 0}
              title="Q&A Practice"
              className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">Q&amp;A Practice</span>
            </button>

            {/* Enrich with AI */}
            {SHOW_BULK_ENRICH && (
              <div className="relative group/enrich">
                <button
                  onClick={handleBulkEnrich}
                  disabled={selectedCardIds.size === 0 || selectedCardIds.size > BULK_ENRICH_LIMIT || isBulkEnriching}
                  title="Enrich with AI"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBulkEnriching ? (
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                  ) : (
                    <Sparkles className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="hidden sm:inline">{isBulkEnriching ? 'Enriching...' : 'Enrich with AI'}</span>
                </button>
                {selectedCardIds.size > BULK_ENRICH_LIMIT && (
                  <div className="absolute right-0 top-full mt-1 z-10 w-48 bg-gray-800 text-white text-xs rounded-md px-2 py-1.5 hidden group-hover/enrich:block">
                    Maximum {BULK_ENRICH_LIMIT} cards at a time
                  </div>
                )}
              </div>
            )}

            {/* Delete */}
            <button
              onClick={handleBulkDelete}
              disabled={selectedCardIds.size === 0 || isBulkDeleting}
              title="Delete"
              className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBulkDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
              ) : (
                <Trash2 className="h-4 w-4 flex-shrink-0" />
              )}
              <span className="hidden sm:inline">{isBulkDeleting ? 'Deleting...' : 'Delete'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Add category input */}
      {showAddCategory && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <AddItemInput
            parentId={null}
            type="folder"
            onSubmit={handleAddCategory}
            onCancel={() => setShowAddCategory(false)}
            placeholder="Add category..."
          />
        </div>
      )}

      {/* Categories */}
      <div className="space-y-4">
        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center border border-gray-200 rounded-lg bg-white px-6">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <Tag className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-lg font-semibold text-gray-800 mb-1">Your library is empty</p>
            <p className="text-sm text-gray-500 mb-6 max-w-xs">
              Categories group your vocabulary — e.g. <span className="font-medium">"Business English"</span> or <span className="font-medium">"Phrasal Verbs"</span>.
            </p>
            {isInstanceOwner && (
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={() => setShowAddCategory(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                >
                  <Tag className="h-4 w-4" />
                  Add Category
                </button>
                <button
                  onClick={() => setShowAIGenerator(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md transition-colors"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate with AI
                </button>
              </div>
            )}
          </div>
        ) : (
          categories.map((category) => (
            <CategoryNode
              key={category.id}
              category={category}
              onToggle={handleToggle}
              onRename={handleRename}
              onDelete={handleDelete}
              onAddChild={() => {}} // Not used anymore
              onMove={handleMove}
              onCategoryUpdate={loadCategories}
              onCategoryRefresh={handleCategoryRefresh}
              categories={categories}
              isInstanceOwner={isInstanceOwner}
              openCategoryViewRequestId={openCategoryViewId}
              onCategoryViewRequestHandled={handleCategoryViewRequestHandled}
              isSelectionMode={isSelectionMode}
              selectedCardIds={selectedCardIds}
              onSelectCard={handleSelectCard}
            />
          ))
        )}
      </div>

      {/* AI Tree Generator Modal */}
      {showAIGenerator && (
        <AITreeGenerator
          onClose={() => setShowAIGenerator(false)}
          onSuccess={handleAIGeneratorSuccess}
          instanceId={selectedInstanceId}
          userEmail={user?.email}
        />
      )}

      {/* Card detail opened from search */}
      {searchCardId && (
        <CardDetail
          cardId={searchCardId}
          onClose={() => setSearchCardId(null)}
          onSave={() => loadCategories({ silent: true })}
          isInstanceOwner={isInstanceOwner}
          onDelete={(id) => { handleDelete(id); setSearchCardId(null); }}
          onMove={handleMove}
          categories={categories}
        />
      )}

      {/* Bulk Q&A Practice Modal */}
      {showBulkContextualPractice && selectedCardIds.size > 0 && (
        <CardSelectionContextualPracticeModal
          cardIds={Array.from(selectedCardIds)}
          onClose={() => setShowBulkContextualPractice(false)}
        />
      )}

      {/* Bulk Generate Text Modal */}
      {showBulkGenerateText && selectedCardIds.size > 0 && (
        <GenerateStoryModal
          cardIds={Array.from(selectedCardIds)}
          categoryId={Array.from(selectedCardCategoryIds)[0]}
          onClose={() => setShowBulkGenerateText(false)}
        />
      )}

      {/* Bulk Move Modal */}
      {showBulkMoveModal && selectedCardIds.size > 0 && (
        <MoveToModal
          item={{
            id: Array.from(selectedCardIds)[0],
            name: `${selectedCardIds.size} cards`,
            type: 'card',
            is_folder: false,
            parent_id: null,
            children: [],
          }}
          categories={categories}
          onClose={() => setShowBulkMoveModal(false)}
          onMove={handleBulkMove}
        />
      )}
    </div>
  );
};